import regex from 'constants/regex';
import { DTP_SIGNUP_URL, LOGIN_ROUTER_ENABLED, LOGIN_RESOLVER_PATH } from 'constants/loginRouter';

/**
 * Sending a clinician to the environment their account lives on, from the address alone
 * (PHX-1295, design §3.2 and §3.6).
 *
 * Two rules govern everything here:
 *
 * 1. **Resolution happens before the password is typed** (on blur, not on submit). A redirect after
 *    the password is entered either throws it away or forwards it to another host, and it lands in
 *    the middle of the destination's TFA ceremony. The timing is the design, not a detail.
 * 2. **Failure is never surfaced.** An address we cannot resolve must look exactly like one that
 *    belongs here: no spinner, no message, no error. Otherwise the login page answers "does this
 *    address belong to a CareSimple user, and where?" for anyone who asks (§3.3). Every failure
 *    path below therefore returns "no answer", and the user signs in where they already are, which
 *    is the correct destination for the majority anyway.
 */

/** The address the destination's login page prefills from. */
export const PREFILL_EMAIL_PARAM = 'email';

/**
 * The resolver sits in front of every sign-in (§4.3), so the client keeps its own budget: a portal
 * having a bad day must not hold up a login form.
 */
const RESOLVER_TIMEOUT_MS = 5000;

interface IResolveLoginResponse {
  /** The clinician portal for the address, or null. Null says nothing about why. */
  portalUrl?: string | null;
}

/**
 * The response decides where a browser navigates, so it is validated as untrusted input: an
 * absolute http(s) URL or nothing.
 */
const toPortalUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Asks **our own API** which environment owns an address, rather than the RPM9000 directly.
 *
 * Same origin, so no CORS and no reason to point the estate's identity service at the public
 * internet to serve one login page. health-api holds the portal conversation server-to-server and
 * rate-limits it where this app already rate-limits its other public route.
 *
 * Deliberately `fetch` and not the app's axios instance: that one attaches the session's bearer
 * token and its interceptors treat a 401 as a reason to log somebody out, and nobody is logged in
 * on this page.
 */
const resolvePortal = async (email: string) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), RESOLVER_TIMEOUT_MS);
  try {
    const response = await fetch(LOGIN_RESOLVER_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      signal: controller.signal
    });
    if (!response.ok) {
      return null;
    }
    const body: IResolveLoginResponse = await response.json();
    return body?.portalUrl ? toPortalUrl(body.portalUrl) : null;
  } catch {
    // Network, timeout, throttled, malformed body: all of them mean "no answer", and "no answer"
    // means the user signs in here. Nothing is logged, because a console error is a disclosure too.
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
};

/**
 * Resolves `email` and, if it belongs to another environment, sends the browser to that portal's
 * `path` with the address prefilled. Resolving to this host, to nothing, or not at all all mean the
 * same thing and do nothing at all.
 *
 * `path` is the destination's own route, not the resolved URL's: every environment runs this same
 * app, so its login and forgot-password paths are the ones we already know. The resolved URL
 * supplies the host.
 */
export const routeEmailToItsPortal = async (email: string, path: string) => {
  if (!LOGIN_ROUTER_ENABLED || !regex.email(email)) {
    return;
  }
  const portal = await resolvePortal(email);
  if (!portal || portal.host === window.location.host) {
    return;
  }
  const destination = new URL(path, portal.origin);
  destination.searchParams.set(PREFILL_EMAIL_PARAM, email);
  window.location.assign(destination.toString());
};

/**
 * The address a router redirect carried over, for the destination's login and forgot-password
 * forms. Read on every environment, flag or no flag: the environment doing the routing is not the
 * one doing the prefilling. Anything that is not an address is ignored rather than reflected back
 * into the form.
 */
export const prefilledEmail = (query: { [key: string]: string }) => {
  const email = query[PREFILL_EMAIL_PARAM];
  return email && regex.email(email) ? email : '';
};

/**
 * Where the front door's signup CTA goes: DTP's own signup route, on DTP's host (§3.5).
 *
 * Returns an empty string when this build is not the front door or has no DTP address configured,
 * which is the caller's signal to render nothing. Validated as a URL rather than trusted, because a
 * malformed value would otherwise send the CTA somewhere unpredictable.
 */
export const dtpSignupHref = (path: string) => {
  if (!LOGIN_ROUTER_ENABLED || !DTP_SIGNUP_URL) {
    return '';
  }
  try {
    const base = new URL(DTP_SIGNUP_URL);
    if (base.protocol !== 'https:' && base.protocol !== 'http:') {
      return '';
    }
    return new URL(path, base.origin).toString();
  } catch {
    return '';
  }
};
